import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Box, Paper } from '@mui/material';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { ArrowLeft } from 'lucide-react';
import { useState, useEffect } from 'react';
import ClientProfileSummary from '@/clients/details/legacy/components/ClientProfileSummary';
import { getClientById } from '@/lib/apiService';
import { Client, DRVRequirements } from '@/types/client';
import { Skeleton } from '@/components/ui/skeleton';
import FeedbackButton from '@/components/feedback/FeedbackButton';
import { FeedbackContext } from '@/components/feedback/FeedbackPanel';
import MacroTargetsSection from './components/MacroTargetsSection';
import MicroTargetsSection from './components/MicroTargetsSection';
import { ClientNameLink, getClientDetailsPath } from '@/components/clients/ClientNameLink';

const NutritionalTargetsPage = () => {

  const { id } = useParams<{ id: string }>();
  const clientId = id || '';
  const [client, setClient] = useState<Client | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // State for collapsible macro targets
  const [macroTargetsOpen, setMacroTargetsOpen] = useState(false);

  // State for collapsible micro targets
  const [microTargetsOpen, setMicroTargetsOpen] = useState(false);

  // State for individual micro target sections
  const [vitaminsSectionOpen, setVitaminsSectionOpen] = useState(true);
  const [mineralsSectionOpen, setMineralsSectionOpen] = useState(true);
  const [miscellaneousSectionOpen, setMiscellaneousSectionOpen] = useState(true);

  // Loading states
  const [isMicroLoading, setIsMicroLoading] = useState<boolean>(false);

  const [micronutrientTargets, setMicronutrientTargets] = useState<any>(null);
  const [macroTargets, setMacroTargets] = useState<any>(null);
  const [drvRequirements, setDrvRequirements] = useState<DRVRequirements | null>(null);

  // Fetch client data on component mount
  useEffect(() => {
    const fetchClientData = async () => {
      if (!clientId) {
        setError('Invalid client ID');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const apiClient = await getClientById(clientId);
        setClient(apiClient.client);
      } catch (err: any) {
        console.error('Error fetching client data:', err);
        setError(err.message || 'Failed to fetch client data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchClientData();
  }, [clientId]);

  useEffect(() => {
    if (!client) return;

    // Use drvRequirements directly - no transformation needed
    if (client.drvRequirements) {
      setDrvRequirements(client.drvRequirements);
    } else {
      // Fallback to old structure if drvRequirements not available
      if (client.micronutrients) {
        setMicronutrientTargets(client.micronutrients);
      }
      if (client.macrosRanges) {
        setMacroTargets(client.macrosRanges);
      }
    }
  }, [client]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-6 w-40 mb-3" />
            <Skeleton className="h-8 w-72" />
          </div>
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-40" />
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="space-y-6">
        <div className="flex items-center">
          <Link to="/clients">
            <Button variant="ghost" size="sm" className="gap-1">
              <ArrowLeft className="h-4 w-4" /> Back to Clients
            </Button>
          </Link>
        </div>
        <div className="text-center py-8">
          <h2 className="text-2xl font-semibold">Client Not Found</h2>
          <p className="text-gray-500 mt-2">The client you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid rgba(15, 23, 42, 0.08)',
          bgcolor: '#fff',
        }}
      >
        <div className="space-y-6 p-4 sm:p-5 lg:px-7 lg:pt-6 lg:pb-7">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1">
          <Link to={getClientDetailsPath(id)}>
            <Button variant="ghost" size="sm" className="gap-1 mb-3">
              <ArrowLeft className="h-4 w-4" /> Back to Client Details
            </Button>
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold text-cs-foreground tracking-tight">
            Nutritional Targets -{' '}
            <ClientNameLink clientId={id} className="text-cs-foreground hover:text-cs-foreground">
              {client.fullName || `${client.firstName || ''} ${client.lastName || ''}`.trim()}
            </ClientNameLink>
          </h1>
        </div>
        <FeedbackButton
          clientId={clientId}
          label="Testing Feedback"
          defaultContext={FeedbackContext.TARGET_NUTRITIONAL_ANALYSIS}
          title="Testing Feedback – Target Nutritional Analysis"
          description="Please provide feedback on the Target Nutritional Analysis section. Please specify the date of feedback. Multiple feedback entries allowed."
          titlePlaceholder="e.g., Nutritional analysis calculation issues"
          detailsPlaceholder="Describe the steps, date of feedback, and observations when reviewing target nutritional analysis."
          submitLabel="Save Client Feedback"
          successDescription="Thanks for helping us improve Target Nutritional Analysis."
        />
      </div>

      {/* Client Profile Summary */}
      <ClientProfileSummary client={client} />

      {/* Content */}
      <div className="grid gap-6">
       
        {/* Macro Targets */}
        <MacroTargetsSection
          drvRequirements={drvRequirements}
          macroTargets={macroTargets}
          isOpen={macroTargetsOpen}
          onOpenChange={setMacroTargetsOpen}
        />

        {/* Micro Targets */}
        <MicroTargetsSection
          drvRequirements={drvRequirements}
          micronutrientTargets={micronutrientTargets}
          isOpen={microTargetsOpen}
          onOpenChange={setMicroTargetsOpen}
          vitaminsSectionOpen={vitaminsSectionOpen}
          onVitaminsSectionOpenChange={setVitaminsSectionOpen}
          mineralsSectionOpen={mineralsSectionOpen}
          onMineralsSectionOpenChange={setMineralsSectionOpen}
          miscellaneousSectionOpen={miscellaneousSectionOpen}
          onMiscellaneousSectionOpenChange={setMiscellaneousSectionOpen}
          isMicroLoading={isMicroLoading}
        />
      </div>
    </div>
      </Paper>
    </Box>
  )
}

export default NutritionalTargetsPage;
